import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Smartphone, Download, ExternalLink, BookOpen, FileText, PlayCircle,
} from 'lucide-react';

/**
 * AdminDocsTile — central library of admin-reference markdown docs
 * (publishing guides, integration playbooks, anything I generate for
 * the user). Files live under `/app/frontend/public/*.md` so they
 * deploy with the React build and are reachable via direct URL.
 *
 * If you add new docs to `/app/frontend/public/*.md`, add them here
 * too — the tile is the single source of truth for "what docs exist".
 */
const DOCS = [
  {
    id: 'iap-setup',
    label: 'Full IAP setup (Apple + Google + Amazon)',
    href: '/IAP_SETUP_GUIDE.md',
    icon: Smartphone,
    description: 'Step-by-step: products, prices, sandbox testers, server validation, env vars.',
  },
  {
    id: 'play-update',
    label: 'Publish a Play Store update',
    href: '/PUBLISH_PLAY_STORE_UPDATE.md',
    icon: PlayCircle,
    description: 'versionCode bump, AAB upload, staged rollout, common rejections.',
  },
  {
    id: 'amazon-update',
    label: 'Publish an Amazon Appstore update',
    href: '/PUBLISH_AMAZON_UPDATE.md',
    icon: Download,
    description: 'APK upload, Amazon IAP product IDs (amzn_…), pre-release channel.',
  },
  {
    id: 'apple-rejection',
    label: 'Apple App Store — rejection fix playbook',
    href: '/APPLE_REJECTION_FIX_MAY2026.md',
    icon: FileText,
    description: 'iOS WKWebView popup fix, native IAP path, 7-day trial badge, Restore Purchases.',
  },
];

const AdminDocsTile = () => (
  <Card data-testid="admin-docs-tile">
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-base">
        <BookOpen className="h-4 w-4 text-[#00A896]" />
        Admin docs
      </CardTitle>
      <CardDescription className="text-xs">
        Reference guides and playbooks we've put together for you — bundled with the deployed app.
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-2">
      {DOCS.map(({ id, label, href, icon: Icon, description }) => (
        <a
          key={id}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded-md border p-3 hover:bg-muted/40 transition-colors"
          data-testid={`docs-link-${id}`}
        >
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-[#00C9B3]/10 p-2 shrink-0">
              <Icon className="h-4 w-4 text-[#00A896]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm flex items-center gap-1.5">
                {label}
                <ExternalLink className="h-3 w-3 opacity-60" />
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="shrink-0 h-7 px-2"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Trigger download via a temporary anchor with download attr.
                const a = document.createElement('a');
                a.href = href;
                a.download = href.split('/').pop();
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }}
              title="Download as .md"
              data-testid={`docs-download-${id}`}
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
          </div>
        </a>
      ))}
    </CardContent>
  </Card>
);

export default AdminDocsTile;
